
import { IResourceLoader, IStyle } from 'flux-definition';
import { AppConfig, Logger, Rectangle, StateService } from 'flux-core';
import { values } from 'lodash';
import { from, Observable, of, forkJoin, combineLatest, merge } from 'rxjs';
import { map, mergeMap, switchMap, mapTo, catchError, take, last } from 'rxjs/operators';
import { DiagramRenderView } from '../../diagram/view/diagram-render-view';
import { AbstractShapeView } from '../../framework/view/abstract-shape-view';
import { AbstractShapeModel } from '../../shape/model/abstract-shape.mdl';
import { ConnectorSVGView } from './svg-connector-view';
import { SVGFont } from './svg-font';
import { ImageSVGView } from './svg-image-view';
import { ShapeSVGView } from './svg-shape-view';
import { DiagramDataModel } from '../../diagram/model/diagram-data.mdl';
import { IDiagramLocator } from '../../diagram/locator/diagram-locator.i';
import uniq from 'lodash/uniq';
import { tiptapCSS, tiptapShapeTextCSS } from './tiptap.css';
import { fromPromise } from 'rxjs/internal-compatibility';

// FIXME: Ignoring as tests are not written for this class
/* istanbul ignore file */
export class DiagramSVGView extends DiagramRenderView {

    /**
     * Regular expression to extract the font-family from the html text
     */
    private regexFontFamily = /font-family="([^"]*)|font-family[ ]*:[^;"']*;?\s*/gm;

    /**
     * True if any shape has rich texsts
     */
    private hasRichTexts: boolean;

    /**
     * Constructor
     * @param locator - DiagramLocator to get the diagram data.
     * @param completes A boolean value. If it is true then this DiagramSVGView instance
     * completes when an SVG is rendered once. If it is false then continue updating
     * whenever the diagram changes without completing the changes. Default is true.
     */
    constructor(
        protected locator: Observable<IDiagramLocator<DiagramDataModel, AbstractShapeModel>>,
        protected resources: IResourceLoader,
        protected state: StateService<any, any>,
        protected completes: boolean = true,
    ) {
        super( state )/* istanbul ignore next */;
    }

    /**
     * The type of shape view that should be created for the diagram view.
     * The type provided for the shape view must extend the ShapeRenderView
     * @override
     */
    protected get shapeViewType(): typeof ShapeSVGView {
        return ShapeSVGView;
    }

    /**
     * The type of connector view that should be created for the diagram view.
     * The type provided for the connector view must extend the ConnectorRenderView
     * @override
     */
    protected get connectorViewType(): typeof ConnectorSVGView {
        return ConnectorSVGView;
    }

    /**
     * The type of image view that should be created for the diagram view.
     * The type provided for the image view must extend the ImageRenderView
     * @override
     */
    protected get imageViewType(): typeof ImageSVGView {
        return ImageSVGView;
    }

    /**
     * The method that populates the diagram view as per the diagram model.
     * TODO: these were piped to 'true' before, check whether it's needed.
     * @override
     */
    public populateDiagram(): Observable<unknown> {
        if ( !this.completes ) {
            return super.populateDiagram();
        }
        return this.locator.pipe(
            take( 1 ),
            switchMap( locator => locator.getCurrentShapes()),
            mergeMap( models => from( models )),
            mergeMap( model => this.handleCreate( model )),
            last(),
        );
    }


    /**
     * Returns the current populated diagram view as an SVG. This will have the SVG
     * of the diagram as populated by the diagram view at the time of this method call.
     *
     * To ensure the diagram has completed populating, wait for the observable return by
     * {@see populateDiagram} method to complete.
     *
     * Idealy, this function should return a SVGSVGElement.
     * It returns a string for the moment due to lack of functionality support
     * on a NodeJS environment. JSDom may be a good candidate to fix this problem on a non-browser
     * env but still it do not have a working implementation to get it working.
     *
     * If need to return a SVGSVGElement, make sure the functionality can be performed on a non-browser env
     * like NodeJS.
     */
    public toSvg( shapeIds?: string[], addWatermark: boolean = false ): Observable<string> {
        return this.locator.pipe(
            take( 1 ),
            switchMap( locator => locator.getDiagramOnce()),
            map( diagram => {
                const bounds = shapeIds ? diagram.getBounds( shapeIds ) : diagram.getBounds();
                const svgViewData: ISvgViewData = {
                    leftOffset: -5,
                    topOffset: -3,
                    widthOffset: 8,
                    heightOffset: 6,
                };
                return !addWatermark? this.getSvg( bounds, svgViewData, shapeIds ) : this.getSvgWithWatermark( bounds, svgViewData, shapeIds );
            }),
        );
    }

    /**
     * Returns the current populated diagram frames bound as an SVG.
     * And also if there are any shape ouside the frames will returns
     * the current populated diagram view as an SVG
     */
    public toSvgFrames( addWatermark = false ): Observable<string[]> {
        return this.locator.pipe(
            take( 1 ),
            switchMap( locator => locator.getDiagramOnce()),
            map( diagram => {
                const frames = diagram.getAllFrames();
                const svgFrames = frames.map( frame => {
                    const frameSvgViewData: ISvgViewData = {
                        leftOffset: 0,
                        topOffset: 0,
                        widthOffset: 0,
                        heightOffset: 0,
                        style: frame.style as IStyle,
                    };
                    const shapeIds = diagram.getAllShapesIdsByFrame( frame.id );
                    return !addWatermark ? this.getSvg( frame.bounds, frameSvgViewData, shapeIds )
                        : this.getSvgWithWatermark( frame.bounds, frameSvgViewData, shapeIds );
                });
                if ( diagram.isShapeOutsideFrames()) {
                    const bounds = diagram.getBounds();
                    const svgViewData: ISvgViewData = {
                        leftOffset: -5,
                        topOffset: -3,
                        widthOffset: 8,
                        heightOffset: 6,
                    };
                    svgFrames.push( !addWatermark ? this.getSvg( bounds, svgViewData ) : this.getSvgWithWatermark( bounds, svgViewData ));
                }
                return svgFrames;
            }),
        );
    }

    public getSlideFrame( slide ) {
        return this.locator.pipe(
            take( 1 ),
            switchMap( locator => locator.getDiagramOnce()),
            map( diagram => {
                const bounds: Rectangle = diagram.shapes[ slide.shapes[ 0 ]].bounds;
                slide.shapes.forEach( s => {
                    bounds.absorb( diagram.shapes[ s ].bounds );
                });
                const svgViewData: ISvgViewData = {
                    leftOffset: 0,
                    topOffset: 0,
                    widthOffset: 0,
                    heightOffset: 0,
                };
                const shapeIds = slide.shapes;
                return this.getSvg( bounds, svgViewData, shapeIds );
            }),
        );
    }

    /**
     * This function gets called whenever a shapes zIndex value changes.
     * @override
     */
    protected handleZIndexChange ( shapeId: string, zIndex: number ): void {
        // NOTE: Currently nothing to be done on the svg view when a zIndex changes
    }

    /**
     * This method gets called when a new shape view has to be added to the diagram.
     * Returned observable will complete without emitting when the shape view is ready.
     */
    protected handleCreate( model: AbstractShapeModel ): Observable<unknown> {
        const shape = this.shapes[ model.id ];
        if ( shape ) {
            throw new Error( 'unexpected error: shape already exists' );
        }
        return combineLatest(
            this.createShape( model ),
            this.locator.pipe(
                    take( 1 ),
                    switchMap( locator => locator.getDiagramOnce()),
                ),
        ).pipe(
            switchMap(([ view, diagram ]) => {
                view.updateModel( model );
                return merge(
                    fromPromise( this.addShape( view )),
                    of( this.render( view )),
                );
            }),
        );
    }

    /**
     * Adds a shape view to the diagram view and makes it
     * ready for rendering.
     * @param shape The shape view to add.
     * @param viewIndex - the order of this shape in the view
     */
    protected addShape( shape: AbstractShapeView ) {
        this.shapes[ shape.id ] = shape;
        const val = shape.initialize() as any;
        if ( val && val instanceof Promise ) {
            return val;
        }
        return Promise.resolve( true );
    }

    /**
     * Override createShape method to attach resources.
     */
    protected createShape( shape: AbstractShapeModel ): Observable<AbstractShapeView> {
        return super.createShape( shape ).pipe(
            switchMap(( view: ShapeSVGView ) => {
                if ( !( view instanceof ShapeSVGView ) || !Object.keys( shape.images ).length ) {
                    return of( view );
                }
                const observables = Object.keys( shape.images ).map( imageId => {
                    const image = shape.images[imageId];
                    const imageUrl = this.getImageUrl( image );
                    return this.resources.load( imageUrl ).pipe(
                        switchMap( data => (
                            this.getImageSize( data, shape.bounds.width * 1.4 ) // 1.4 is to maitain quality
                                .then(( value: any ) => {
                                    view.images[image.file] = { data: value.data, size: value };
                                })
                        )),
                        catchError( err => {
                            Logger.error( err );
                            return of( null );
                        }),
                    );
                });
                return forkJoin( ...observables ).pipe(
                    mapTo( view ),
                );
            }),
        );
    }

    /**
     * Retrieves the url of the image (applicable for image imports only)
     */
    private getImageUrl( imageModel: any ): string {
        if ( imageModel.fileType && imageModel.fileType === 'url' ) {
            return AppConfig.get( 'IMAGE_PROXY' ) + imageModel.file;
        }
        if ( imageModel.fileType && imageModel.fileType === 'shapesassets' ) {
            return './assets/images/shapes-assets/' + imageModel.file;
        }
        return AppConfig.get( 'CUSTOM_IMAGE_BASE_URL' ) + imageModel.file;
    }

    /**
     * Gets the image width and height from the base64 image source.
     */
    private getImageSize( data: string, expectedWidth: number ): Promise<{ data, x, y, width, height }> {
        return new Promise(( resolve, reject ) => {
            const img = new Image();
            img.onerror = err => reject( err );
            img.onload = () => {
                // if the image width is way greater than the expoected size
                // scale down the image to expected width
                if ( img.width - expectedWidth  > 100  ) {
                    // Calculate the aspect ratio of the original image
                    const aspectRatio = img.width / img.height;

                    // Calculate the new height based on the expected width and aspect ratio
                    const expectedHeight = expectedWidth / aspectRatio;

                    // Create a canvas element with the new dimensions
                    const canvas = document.createElement( 'canvas' );
                    canvas.width = expectedWidth;
                    canvas.height = expectedHeight;

                    // Draw the original image onto the canvas with the new dimensions
                    const context = canvas.getContext( '2d' );
                    context.imageSmoothingEnabled = true;
                    context.drawImage( img, 0, 0, expectedWidth, expectedHeight );
                    // Get the new base64 string representation of the scaled-down image
                    const newBase64String = canvas.toDataURL();
                    resolve({ data: newBase64String, x: 0, y: 0, width: expectedWidth, height: expectedHeight });
                } else {
                    resolve({ data, x: 0, y: 0, width: img.width, height: img.height });
                }
            };
            img.src = data;
        });
    }

    /**
     * Populate a SVG string for given bounds and shape data
     */
    private getSvg(
        bounds: Rectangle, svgViewData: ISvgViewData, shapeIds?: string[]): string {
        let svg = `<svg xmlns="http://www.w3.org/2000/svg" xmlns:svg="http://www.w3.org/2000/svg" ` +
                `xmlns:xlink="http://www.w3.org/1999/xlink" version="1.0" ` +
                `height="${bounds.height}pt" width="${bounds.width}pt" ` +
                `viewBox="${bounds.left + svgViewData.leftOffset } ${bounds.top + svgViewData.topOffset } ` +
                `${bounds.width + svgViewData.widthOffset} ${bounds.height + svgViewData.heightOffset}" ` +
                `${ this.getBackgroundStyle( svgViewData.style ) }>`;
        const allShapeData = this.getAllShapeData( shapeIds );
        const fontFamilies = this.extractFontFamily( allShapeData );
        svg += SVGFont.fontFaces( fontFamilies );
        svg += `<defs>
        <style>${ this.getStyles() }</style>
        ${ this.getShadowFilters()}
        </defs>`;
        svg += allShapeData;
        return svg + '</svg>';
    }

    private getSvgWithWatermark( bounds: Rectangle, svgViewData: ISvgViewData, shapeIds?: string[]): string {
        const _wmBounds = { width: 200, height: 80 };
        let scale = 1;
        if ( _wmBounds.width > bounds.width ) {
            scale = bounds.width / _wmBounds.width;
        }
        if ( _wmBounds.height > bounds.height ) {
            scale = Math.min( bounds.height / _wmBounds.height, scale || 1 );
        }

        const wmBounds = {
            width: _wmBounds.width * scale,
            height: _wmBounds.height * scale,
        }
        const x = bounds.x + bounds.width / 2 - wmBounds.width / 2;
        const y = bounds.y + bounds.height - wmBounds.height;

        const waterMark = `<g xmlns="http://www.w3.org/2000/svg" id="HIu3KiFYXgP"><g transform="translate(${x} ${y})"><image style="opacity: 0.35;" width="${wmBounds.width}" height="${wmBounds.height}" xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="data:image/svg+xml;base64,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" preserveAspectRatio="none"/></g><g transform="translate(-9060 -4770)"><rect x="0" y="0" width="200" height="80" fill="none" stroke-dasharray="0 0"/></g><g transform="translate(-8960, -4675) rotate(0, 0, 0)" width="0" height="36"><text text-decoration="none" x="0" fill="#4b4b4b" font-size="10pt" font-family="noto_regular" y="14"/><text text-decoration="none" x="0" fill="#4b4b4b" font-size="10pt" font-family="noto_regular" y="34"/></g></g>`
        let svg = `<svg xmlns="http://www.w3.org/2000/svg" xmlns:svg="http://www.w3.org/2000/svg" ` +
                `xmlns:xlink="http://www.w3.org/1999/xlink" version="1.0" ` +
                `height="${bounds.height}pt" width="${bounds.width}pt" ` +
                `viewBox="${bounds.left + svgViewData.leftOffset } ${bounds.top + svgViewData.topOffset } ` +
                `${bounds.width + svgViewData.widthOffset} ${bounds.height + svgViewData.heightOffset}" ` +
                `${ this.getBackgroundStyle( svgViewData.style ) }>`;
        const allShapeData = this.getAllShapeData( shapeIds );
        const fontFamilies = this.extractFontFamily( allShapeData );
        svg += SVGFont.fontFaces( fontFamilies );
        svg += `<defs>
        <style>${ this.getStyles() }</style>
        ${ this.getShadowFilters()}
        </defs>`;
        svg += allShapeData;
        svg += waterMark;
        return svg + '</svg>';
    }

    /**
     * This function returns the concatenated svg data for the given
     * list of shape Ids.
     * @returns - SVG string with Shape data.
     */
    private getAllShapeData( shapeIds?: string[]) {
        const shapes: AbstractShapeView[] =
                shapeIds ? this.getShapeViewsByShapeIds( shapeIds ) : values( this.getShapeViews());
        // Sort the shapes by zindex to make sure they are in correct order
        shapes.sort(( s1, s2 ) => s1.model.zIndex - s2.model.zIndex );
        let svg = '';
        for ( const shape of shapes ) {
            if ( !this.hasRichTexts && Object.values( shape.model.texts ).find(
                ( t: any ) => ( t.rendering === 'tiptapCanvas' || t.rendering === 'dom' ))
            ) {
                this.hasRichTexts = true;
            }
            svg += shape.renderedShape;
        }
        return svg;
    }

    /**
     * This function extracts the font-family names from the given html/svg string.
     * @param text - the font-family names to be extracted from.
     * @returns - a string array of font-family names.
     */
    private extractFontFamily( text: string ): string[] {
        let matches;
        const fontFamilyNames = [];
        while (( matches = this.regexFontFamily.exec( text ))) {
            fontFamilyNames.push( matches[0].replace( /\s|;|font-family|:|"|=/g, '' ));
        }
        return uniq( fontFamilyNames );
    }

    /**
     * Get background style from background shape and
     * add those style data into svg string
     * @param style shape style
     * @returns attribute string
     */
    private getBackgroundStyle( style?: IStyle ) {
        let backgroundStyle = style && style.fillColor ? `fillColor="${style.fillColor}" ` : ``;
        if ( style && style.fillGradient ) {
            backgroundStyle += `fillGradient:colors="${( style.fillGradient.colors ).join( ' ' )}" `;
            backgroundStyle += `fillGradient:ratios="${( style.fillGradient.ratios ).join( ' ' )}" `;
            backgroundStyle += `fillGradient:x0="${style.fillGradient.x0}" `;
            backgroundStyle += `fillGradient:x1="${style.fillGradient.x1}" `;
            backgroundStyle += `fillGradient:y0="${style.fillGradient.y0}" `;
            backgroundStyle += `fillGradient:y1="${style.fillGradient.y1}" `;
        }
        return backgroundStyle;
    }

    private getShadowFilters ( shapeIds?: string[]) {
        const shapes: AbstractShapeView[] =
        shapeIds ? this.getShapeViewsByShapeIds( shapeIds ) : values( this.getShapeViews());
        shapes.sort(( s1, s2 ) => s1.model.zIndex - s2.model.zIndex );
        let svg = '';
        for ( const shape of shapes ) {
                if ( shape.model.style && shape.model.style.shadow ) {
                    svg += ` <filter id="shadow${shape.model.id}"><feDropShadow dx="${shape.model.style.shadow.offsetX}" dy="${shape.model.style.shadow.offsetY}" stdDeviation="${shape.model.style.shadow.blur / 10}" flood-color="${shape.model.style.shadow.color}" /></filter>`;
                }
            }
        return svg;
    }

    /**
     * Common styles added to the svg
     */
     private getStyles() {
        return `
            span{ display: inline-block; }
            ${ this.hasRichTexts ? this.getRichtextStyles() : '' }
        `;
    }

    private getRichtextStyles() {
        return `
            /* Copied from tiptap.scss */
            ${tiptapCSS}

            /* Styles specific to shape text editor*/
            ${tiptapShapeTextCSS}
            `;
    }
}

/**
 * This hold data which use in svg export
 */
export interface ISvgViewData {

    /**
     * left offset value
     */
    leftOffset: number;

    /**
     * top offset value
     */
    topOffset: number;

    /**
     * width offset value
     */
    widthOffset: number;

    /**
     * height offset value
     */
    heightOffset: number;

    /**
     * background shape style
     */
    style?: IStyle;

}
